import type { Problem } from '@/shared/generated'

import { isAxiosError } from 'axios'
import { AlertTriangleIcon, HourglassIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	isDemandBlocked,
	isLifecycleBlocked,
	normalizeReasons,
	useMidTermForecastQuery,
	useMidTermPredictionStatusQuery,
	useMidTermSegmentationQuery,
} from '@/entities/mid-term-prediction'
import { useOptimizationQuery } from '@/entities/optimization'

import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'

import { useDemandDriversQuery } from '../api/useDemandDriversQuery'
import { DemandDriversCard } from './DemandDriversCard'
import { DemandPatternCard } from './DemandPatternCard'
import { ForecastSection } from './ForecastSection'
import { LifecycleCard } from './LifecycleCard'

export const MidTermForecastPanel = ({
	lineId,
	optimizationGroupId,
}: {
	lineId: string
	optimizationGroupId: string
}) => {
	const { t } = useTranslation()
	const statusQuery = useMidTermPredictionStatusQuery({
		refetchOnMount: 'always',
	})
	const { data: optimization } = useOptimizationQuery(optimizationGroupId)
	const optimizationId = optimization?.id
	const params = {
		optimizationGroupId,
		optimizationId: optimizationId!,
		pricingLineId: lineId,
	}
	const isReady = !!statusQuery.data?.is_ready
	const enabled = !!optimizationId && isReady

	const forecastQuery = useMidTermForecastQuery(params, { enabled })
	const segmentationQuery = useMidTermSegmentationQuery(params, { enabled })
	const driversQuery = useDemandDriversQuery({
		optimizationGroupId,
		lineId,
		enabled,
	})

	if (statusQuery.isError) {
		const { error } = statusQuery
		const problem = isAxiosError<Problem>(error) ? error.response?.data : null

		return (
			<Empty data-testid='mtf-status-error'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<AlertTriangleIcon />
					</EmptyMedia>
					<EmptyDescription>
						{problem?.detail ||
							problem?.title ||
							t('midterm_forecast.status_error', {
								defaultValue: 'Failed to check mid-term forecast availability.',
							})}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	if (!statusQuery.data) return null

	if (!isReady) {
		return (
			<Empty data-testid='mtf-not-ready'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<HourglassIcon />
					</EmptyMedia>
					<EmptyDescription>
						{t('midterm_forecast.not_ready', {
							defaultValue:
								'Forecast data is being prepared. Check back later.',
						})}
					</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}

	const reasons = forecastQuery.data ? normalizeReasons(forecastQuery.data) : []
	const segmentationPending =
		!segmentationQuery.isSuccess && !segmentationQuery.isError

	return (
		<div className='grid gap-5' data-testid='mtf-panel'>
			<ForecastSection
				forecast={forecastQuery.data}
				isPending={!forecastQuery.isSuccess && !forecastQuery.isError}
				isError={forecastQuery.isError}
			/>
			<section className='flex flex-col gap-2'>
				<h3 className='text-sm font-semibold'>
					{t('midterm_forecast.why_title', {
						defaultValue: 'Why this forecast',
					})}
				</h3>
				<DemandPatternCard
					demand={segmentationQuery.data?.demand}
					blocking={isDemandBlocked(reasons)}
					isPending={segmentationPending}
					isError={segmentationQuery.isError}
				/>
				<LifecycleCard
					lifecycle={segmentationQuery.data?.lifecycle}
					blocking={isLifecycleBlocked(reasons)}
					isPending={segmentationPending}
					isError={segmentationQuery.isError}
				/>
				<DemandDriversCard
					data={driversQuery.data}
					isPending={driversQuery.isPending}
					isError={driversQuery.isError}
				/>
			</section>
		</div>
	)
}
